import { scaleLinear } from 'd3-scale'
import { CircleCheck } from 'lucide-react'
import { PremiumGate } from '../../../platform/access/PremiumGate'
import type { AsyncState } from '../../../platform/async/Async'
import { Skeleton } from '../../../platform/async/Skeleton'
import { fixedDecimals } from '../../../platform/fixedDecimals'
import { Icon } from '../../../platform/icons/Icon'
import { Money } from '../../../platform/primitives/Money'
import { MONTH_SHORT } from '../../cashflow/cashflowPeriod'
import type { Insight } from '../../insights/insightsWire'
import { OverviewWidget } from '../OverviewWidget'
import { SEVERITY_TAG, type InsightView } from './insightView'

const SLOT_W = 20
const GAP = 4
const H = 32
/** A zero month still draws a hairline so the strip reads as twelve months. */
const FLOOR = 1.5

const monthName = (month: string): string => MONTH_SHORT[Number(month.slice(5, 7)) - 1] ?? month

/**
 * The server's twelve monthly totals for the category, on a linear scale from
 * zero to the largest. That is geometry, not a money result. The current month
 * is the LAST point (as the wire says) and takes the severity fill. The bars are
 * `aria-hidden`; the same figures are a text list, so no amount lives only in a
 * picture.
 */
function InsightStrip({ insight, currency }: { insight: Insight; currency: string }) {
  const { series, severity } = insight
  const tallest = Math.max(1, ...series.map((p) => p.amountCents))
  const y = scaleLinear().domain([0, tallest]).range([FLOOR, H])
  const last = series.length - 1
  return (
    <div className="ov-insight__strip">
      <svg
        className="ov-insight__plot"
        viewBox={`0 0 ${series.length * SLOT_W} ${H}`}
        preserveAspectRatio="none"
        aria-hidden="true"
        focusable="false"
      >
        {series.map((p, i) => {
          const h = y(p.amountCents)
          return (
            <rect
              key={p.month}
              className={i === last ? `ov-insight__bar ov-insight__bar--${severity}` : 'ov-insight__bar'}
              x={fixedDecimals(i * SLOT_W + GAP / 2, 2)}
              y={fixedDecimals(H - h, 2)}
              width={SLOT_W - GAP}
              height={fixedDecimals(h, 2)}
              data-chart
            />
          )
        })}
      </svg>
      <ul className="sr-only" aria-label="Spending by month">
        {series.map((p, i) => (
          <li key={p.month} aria-current={i === last ? 'date' : undefined}>
            <span>{`${monthName(p.month)}: `}</span>
            <Money cents={p.amountCents} currency={currency} kind="stock" />
            {i === last && <span>{' (this month)'}</span>}
          </li>
        ))}
      </ul>
    </div>
  )
}

/** A picture in the card's anatomy, with no text or figure in it, so it fabricates no insight. */
function InsightPreview() {
  // Declared here, never at module scope: an eager constant ships in the bundle.
  const heights = [8, 8, 8, 8, 8, 14, 14, 14, 14, 14, 14, 28]
  return (
    <div className="ov-insight__preview" aria-hidden="true">
      <span className="ov-insight__stub ov-insight__stub--tag" />
      <span className="ov-insight__stub" />
      <span className="ov-insight__stub ov-insight__stub--short" />
      <svg className="ov-insight__plot" viewBox={`0 0 ${heights.length * SLOT_W} ${H}`} preserveAspectRatio="none" focusable="false">
        {heights.map((h, i) => (
          // The heights repeat, so the slot is the identity: the list never reorders.
          <rect key={i} className="ov-insight__bar" x={i * SLOT_W + GAP / 2} y={H - h} width={SLOT_W - GAP} height={h} />
        ))}
      </svg>
    </div>
  )
}

/**
 * The Insight card: the server's first (most severe) category insight as
 * written, with the reason it was shown and the category's twelve-month strip.
 * Every sentence and figure is the server's (no client arithmetic). A free user
 * sees the locked preview and nothing of their data: the real children are never
 * rendered.
 */
export function InsightWidget({ state, entitled, cta }: { state: AsyncState<InsightView>; entitled: boolean; cta: 'none' | 'overlay' }) {
  if (!entitled) {
    return (
      <OverviewWidget<null> title="Insight" surface="overview-insight" state={{ status: 'success', data: null }} skeleton={null} empty={{ title: '' }}>
        {() => (
          <PremiumGate entitlement="spending_insights" cta={cta} panelKind="insight" preview={<InsightPreview />}>
            {null}
          </PremiumGate>
        )}
      </OverviewWidget>
    )
  }
  const tag =
    state.status === 'success' ? (
      <span className={`ov-insight__tag ov-insight__tag--${state.data.insight.severity}`}>{SEVERITY_TAG[state.data.insight.severity]}</span>
    ) : undefined
  return (
    <OverviewWidget<InsightView>
      title="Insight"
      surface="overview-insight"
      state={state}
      fill
      headExtra={tag}
      skeleton={
        <>
          <Skeleton height={18} width={80} />
          <Skeleton height={13} />
          <Skeleton height={13} />
          <Skeleton height={32} />
        </>
      }
      empty={{
        title: 'Nothing needs your attention',
        body: 'Each category is checked against your usual month.',
        icon: (
          <span className="ov-insight__ok">
            <Icon glyph={CircleCheck} size={20} />
          </span>
        ),
      }}
    >
      {({ insight, currency }) => (
        <>
          <p className="ov-insight__title">{insight.title}</p>
          <p className="ov-insight__body">{insight.body}</p>
          <p className="note ov-insight__why">{insight.why}</p>
          <InsightStrip insight={insight} currency={currency} />
        </>
      )}
    </OverviewWidget>
  )
}
